import { Alert, Button, CircularProgress, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import Container from 'src/components/atoms/Container';
import { StorefrontTypeSelect } from 'src/components/molecules/StorefrontTypeSelect/StorefrontTypeSelect';
import { SmartModuleTitleCard } from 'src/components/organisms/SmartModuleTitleCard/SmartModuleTitleCard';
import { useChainId } from 'src/hooks';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useSnackbar } from 'src/hooks/snackbar/useSnackbar';
import { useStorefrontType } from 'src/hooks/style-timeline/useStorefrontType';
import { useStoreType } from 'src/hooks/useStoreType';
import { recordError } from 'src/infrastructure/telemetry';
import { getErrorMessage, isClientError } from 'src/utils/api/error-utils';
import { handleDownload, handleUpload } from 'src/utils/fileUtils';

export const StyleFamilyNameEditor: FC = () => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const showSnackbar = useSnackbar();
    const showChangesSnackbar = useChangesSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState<string | null>(null);

    const chainId = useChainId();
    const storeType = useStoreType();
    const storefrontType = useStorefrontType();

    const params = useMemo(
        () =>
            new URLSearchParams({
                storeType,
                storefrontType,
            }),
        [storeType, storefrontType]
    );

    const startDownload = useCallback(() => {
        setIsLoading(true);
        setLoadingText('Downloading style family names...');
        handleDownload('stylefamily_names.csv', `/api/stylefamilyname/chains/${chainId}?${params}`, 'GET')
            .then(() => {
                showSnackbar('Download successful', {
                    variant: 'success',
                    autoHideDuration: 3000,
                    preventDuplicate: true,
                });
            })
            .catch((err) => {
                showSnackbar(getErrorMessage(err, 'Could not download style family names'), {
                    variant: 'error',
                    preventDuplicate: true,
                });
                !isClientError(err) && recordError(err);
            })
            .finally(() => {
                setIsLoading(false);
                setLoadingText(null);
            });
    }, [chainId, params, showSnackbar]);

    const startUpload = useCallback(
        (file: File | undefined) => {
            if (!file) {
                return;
            }

            setIsLoading(true);
            setLoadingText('Uploading style family names...');

            const formData = new FormData();
            formData.append('file', file);

            handleUpload(`/api/stylefamilyname/chains/${chainId}`, 'PUT', formData)
                .then(() => {
                    showChangesSnackbar();
                })
                .catch((err) => {
                    showChangesSnackbar(err);
                })
                .finally(() => {
                    setIsLoading(false);
                    setLoadingText(null);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                });
        },
        [chainId, showChangesSnackbar]
    );

    return (
        <Stack spacing={2}>
            <SmartModuleTitleCard />
            <Container>
                <Stack
                    sx={{
                        gap: 2,
                    }}
                >
                    <Alert severity="info">
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                            }}
                        >
                            How to use the editor
                        </Typography>
                        <ol>
                            <li>
                                <Typography>
                                    Download the most recent version of the CSV file, by clicking the download button
                                </Typography>
                            </li>
                            <li>
                                <Typography>Open the CSV file in a spreadsheet editor (e.g. Excel)</Typography>
                            </li>
                            <li>
                                <Typography>
                                    Make the necessary changes in the spreadsheet. Change only the{' '}
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        StyleFamilyName
                                    </Typography>{' '}
                                    column.
                                </Typography>
                            </li>
                            <li>
                                <Typography>Save the file</Typography>
                            </li>
                            <li>
                                <Typography>Upload the file by clicking the upload button</Typography>
                            </li>
                        </ol>
                    </Alert>

                    <StorefrontTypeSelect />
                    <Stack
                        direction="row"
                        sx={{
                            gap: 2,
                        }}
                    >
                        <Button onClick={startDownload} fullWidth variant="contained" color="info">
                            Download CSV
                        </Button>
                        <Button component="label" fullWidth variant="contained" color="info">
                            Upload CSV
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                onChange={(e) => startUpload(e.target.files?.[0])}
                            />
                        </Button>
                    </Stack>
                </Stack>
            </Container>
            <Dialog open={isLoading}>
                <DialogContent>
                    <Stack
                        direction="row"
                        sx={{
                            gap: 2,
                            alignItems: 'center',
                        }}
                    >
                        <CircularProgress />
                        <Typography>{loadingText}</Typography>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Stack>
    );
};
